import { useMemo, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  CheckSquare,
  Hash,
  List,
  Star,
  ThumbsUp,
  Type,
  User,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { NpsQuestion, NpsSurvey, QuestionResponseType } from '@/shared/types/nps';
import { Wizard, type WizardStep } from '@/shared/components/wizard';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { submitAnswer } from '../api/use-nps';
import { partitionSurveyQuestions, contactFieldKind, isValidEmail, isValidPhone } from './partition-questions';
import { PublicSurveyUserInfoStep } from './public-survey-user-info-step';
import {
  PublicSurveyQuestionField,
  cpfDigits,
  isAnswerEmpty,
  maskCpf,
  type AnswerValue,
} from './public-survey-question-field';
import { PublicSurveySubmitting } from './public-survey-submit-celebration';

const TYPE_ICONS: Record<QuestionResponseType, LucideIcon> = {
  RANGE: Hash,
  STAR: Star,
  BOOLEAN: ThumbsUp,
  TEXT: Type,
  SELECT: List,
  DATE: Calendar,
  CHECKBOX: CheckSquare,
};

function shortTitle(title: string, max = 18) {
  return title.length > max ? `${title.slice(0, max)}…` : title;
}

function questionRequired(q: NpsQuestion) {
  return q.required ?? true;
}

function stepCard(content: ReactNode) {
  return (
    <div className="glass space-y-4 rounded-2xl p-4 sm:space-y-5 sm:p-6 md:p-8">{content}</div>
  );
}

interface PublicSurveyWizardProps {
  survey: NpsSurvey;
  lang?: string;
  identifierFromUrl?: string;
  /** Respostas já conhecidas (ex.: nome/e-mail/celular de quem está logado na área do participante). */
  initialAnswers?: Record<string, AnswerValue>;
  /** Pula a etapa "Seus dados" quando TODAS as perguntas dela vieram preenchidas e válidas (as respostas vão no envio). */
  skipPrefilledUserInfo?: boolean;
  /** Stepper só com as bolinhas, sem os títulos das etapas. */
  compact?: boolean;
  onSubmittingChange?: (submitting: boolean) => void;
  onSuccess: () => void;
}

function validContact(q: NpsQuestion, value: AnswerValue): boolean {
  if (isAnswerEmpty(value) || typeof value !== 'string') return false;
  const kind = contactFieldKind(q.title);
  if (kind === 'email') return isValidEmail(value);
  if (kind === 'phone') return isValidPhone(value);
  return value.trim().length > 0;
}

export function PublicSurveyWizard({
  survey,
  lang,
  identifierFromUrl = '',
  initialAnswers,
  skipPrefilledUserInfo = false,
  compact = false,
  onSubmittingChange,
  onSuccess,
}: PublicSurveyWizardProps) {
  const { t, i18n } = useTranslation();
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>(() => {
    const initial: Record<string, AnswerValue> = {};
    for (const q of survey.questions ?? []) {
      if (q.response_type === 'BOOLEAN') initial[q.id] = true;
    }
    return { ...initial, ...initialAnswers };
  });
  const [identifier, setIdentifier] = useState(identifierFromUrl ? maskCpf(identifierFromUrl) : '');
  const [submitting, setSubmitting] = useState(false);

  const { userInfo, rest } = useMemo(
    () => partitionSurveyQuestions(survey.questions ?? []),
    [survey.questions],
  );

  const allQuestions = useMemo(() => [...userInfo, ...rest], [userInfo, rest]);

  // Decidido uma vez, com as respostas iniciais: a etapa não some no meio se a pessoa editar algo.
  const [skipUserInfo] = useState(
    () =>
      skipPrefilledUserInfo &&
      userInfo.length > 0 &&
      userInfo.every((q) => validContact(q, initialAnswers?.[q.id])),
  );

  const needsIdentifierStep =
    survey.response_type === 'IDENTIFIED' && !identifierFromUrl;

  const setAnswer = (questionId: string, value: AnswerValue) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const wizardLabels = useMemo(
    () => ({
      cancel: t('sharedWizard.cancel'),
      back: t('sharedWizard.back'),
      next: t('sharedWizard.next'),
      stepOf: t('sharedWizard.stepOf'),
      stepAria: t('sharedWizard.stepAria'),
      backToStep: t('sharedWizard.backToStep'),
    }),
    [t],
  );

  const steps: WizardStep[] = useMemo(() => {
    const built: WizardStep[] = [];

    if (userInfo.length > 0 && !skipUserInfo) {
      built.push({
        id: 'user-info',
        title: t('wizard.userData'),
        icon: Users,
        validate: () => {
          for (const q of userInfo) {
            const value = ((answers[q.id] as string) ?? '').trim();
            const required = questionRequired(q);
            const kind = contactFieldKind(q.title);

            if (required && isAnswerEmpty(answers[q.id])) {
              toast.error(t('validation.requiredField', { field: q.title }));
              return false;
            }
            if (!value) continue;

            if (kind === 'email' && !isValidEmail(value)) {
              toast.error(t('validation.invalidEmail'));
              return false;
            }
            if (kind === 'phone' && !isValidPhone(value)) {
              toast.error(t('validation.invalidPhone'));
              return false;
            }
          }
          return true;
        },
        content: (
          <PublicSurveyUserInfoStep
            questions={userInfo}
            answers={answers}
            onChange={setAnswer}
          />
        ),
      });
    }

    for (const q of rest) {
      const isTextarea = q.response_type === 'TEXT' && q.response_metadata?.input_type === 'textarea';
      built.push({
        id: q.id,
        title: shortTitle(q.title),
        icon: TYPE_ICONS[q.response_type],
        validate: () => {
          if (!questionRequired(q)) return true;
          if (isAnswerEmpty(answers[q.id])) {
            toast.error(t('validation.answerRequired'));
            return false;
          }
          return true;
        },
        content: stepCard(
          <>
            <div className="space-y-1 text-center">
              <h2 className="text-lg font-semibold tracking-tight sm:text-xl">{q.title}</h2>
              {q.description ? (
                <p className="text-sm text-muted-foreground">{q.description}</p>
              ) : null}
            </div>
            <PublicSurveyQuestionField
              q={q}
              value={answers[q.id]}
              onChange={(v) => setAnswer(q.id, v)}
              mobile
              enterSubmits={!isTextarea}
            />
          </>,
        ),
      });
    }

    if (needsIdentifierStep) {
      built.push({
        id: 'identifier',
        title: t('wizard.identification'),
        icon: User,
        validate: () => {
          const digits = cpfDigits(identifier);
          if (survey.identification_required && digits.length !== 11) {
            toast.error(t('validation.invalidCpf'));
            return false;
          }
          if (digits.length > 0 && digits.length !== 11) {
            toast.error(t('validation.incompleteCpf'));
            return false;
          }
          return true;
        },
        content: stepCard(
          <>
            <div className="space-y-1 text-center">
              <h2 className="text-lg font-semibold">{t('wizard.identification')}</h2>
              <p className="text-sm text-muted-foreground">{t('wizard.identificationHint')}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="survey-cpf">{t('wizard.cpfLabel')}</Label>
              <Input
                id="survey-cpf"
                value={identifier}
                onChange={(e) => setIdentifier(maskCpf(e.target.value))}
                placeholder={t('placeholders.cpf')}
                inputMode="numeric"
                enterKeyHint="go"
                className="h-12 text-base"
              />
            </div>
          </>,
        ),
      });
    }

    return built;
  }, [
    userInfo,
    skipUserInfo,
    rest,
    answers,
    identifier,
    needsIdentifierStep,
    survey.identification_required,
    t,
  ]);

  const handleFinish = async () => {
    setSubmitting(true);
    onSubmittingChange?.(true);
    try {
      const idValue = identifierFromUrl || cpfDigits(identifier);
      await submitAnswer(
        survey.id,
        {
          identifier: idValue || undefined,
          answers: allQuestions
            .filter((q) => !isAnswerEmpty(answers[q.id]))
            .map((q) => ({ question_id: q.id, value: answers[q.id] })),
        },
        lang ?? i18n.language,
      );
      onSuccess();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('validation.submitError');
      toast.error(message);
      setSubmitting(false);
      onSubmittingChange?.(false);
    }
  };

  if (steps.length === 0) {
    return (
      <p className="text-center text-sm text-muted-foreground">{t('wizard.noQuestions')}</p>
    );
  }

  return (
    <Wizard
      steps={steps}
      onFinish={handleFinish}
      isSubmitting={submitting}
      finishLabel={t('wizard.submit')}
      finishLoadingLabel={t('wizard.submitting')}
      labels={wizardLabels}
      hideFirstStepCancel
      compactStepper={compact || steps.length > 5}
      lockNavigationWhileSubmitting
      submittingOverlay={<PublicSurveySubmitting />}
      advanceOnEnter
      mobileOptimized
      clickableStepper
    />
  );
}
